import React, { useState } from 'react';
import { Container, Typography, Box, Paper, TextField, Button, Grid, Chip, Stack } from '@mui/material';
import CTAButton from '../components/common/CTAButton';

function DemoPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    useCase: '',
    timeframe: '',
  });
  const [status, setStatus] = useState(null);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.useCase.trim()) {
      setStatus({ type: 'error', text: 'Please add at least email and use case.' });
      return;
    }
    setStatus({ type: 'success', text: 'Thanks! We’ll follow up shortly.' });
    // In a real flow, post to backend; here we just reset the form
    setForm({ name: '', email: '', company: '', useCase: '', timeframe: '' });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          🚀 Launch your best RL agent in 7 days
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Book a live, high-impact walkthrough. We’ll use <em>your</em> symbols, target KPIs, and deploy a production-ready agent fast.
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }} flexWrap="wrap">
          <Chip label="1-week pilot on your symbols" color="primary" variant="outlined" />
          <Chip label="Time-to-first-model: < 1 day" color="success" variant="outlined" />
          <Chip label="Enterprise guardrails: audit, roles, circuit breakers" variant="outlined" />
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              background: 'var(--card)',
              borderColor: 'var(--border)',
              boxShadow: 'var(--glow)',
              color: 'var(--text)',
            }}
          >
            <Typography variant="h6">Quick booking</Typography>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
              />
              <TextField
                required
                label="Work email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                fullWidth
              />
              <TextField
                label="Company"
                value={form.company}
                onChange={(e) => handleChange('company', e.target.value)}
                fullWidth
              />
              <TextField
                required
                label="Use case / goals"
                value={form.useCase}
                onChange={(e) => handleChange('useCase', e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Timeline (e.g., Q1 launch)"
                value={form.timeframe}
                onChange={(e) => handleChange('timeframe', e.target.value)}
                fullWidth
              />
              {status && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    backgroundColor: status.type === 'success' ? 'success.light' : 'error.light',
                    color: status.type === 'success' ? 'success.contrastText' : 'error.contrastText',
                  }}
                >
                  {status.type === 'success' ? '✅' : '⚠️'} {status.text}
                </Paper>
              )}
              <Stack
                direction="column"
                spacing={3}
                alignItems="center"
                justifyContent="center"
                sx={{ mt: 2 }}
              >
                <CTAButton
                  type="submit"
                  fullWidth
                  size="large"
                  sx={{
                    width: '100%',
                    maxWidth: 320,
                    minWidth: 220,
                  }}
                >
                  📤 Submit request
                </CTAButton>
                <CTAButton
                  href="/contact"
                  fullWidth
                  size="large"
                  sx={{
                    width: '100%',
                    maxWidth: 320,
                    minWidth: 220,
                    mt: 0,
                  }}
                >
                  🗓 Talk to sales
                </CTAButton>
              </Stack>
            </form>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}
              >
                <Typography variant="h6" gutterBottom>
                  What you’ll see (live)
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">• Model management & one-click deploy</Typography>
                  <Typography variant="body2">• Training flows: standard + evolutionary</Typography>
                  <Typography variant="body2">• Competition leaderboards & benchmarking</Typography>
                  <Typography variant="body2">• Live portfolio: trades, P&L, autonomous status</Typography>
                  <Typography variant="body2">• Data/guardrails: how we plug into your stack</Typography>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}
              >
                <Typography variant="h6" gutterBottom>
                  Why teams book (and stay)
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="body2">• 1-week pilot with your symbols/timeframes</Typography>
                  <Typography variant="body2">• Best-performing agent deployed in one click</Typography>
                  <Typography variant="body2">• Enterprise controls: audit, roles, circuit breakers</Typography>
                  <Typography variant="body2">• Measurable KPIs: Sharpe, DD, win rate, latency</Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label="Median ROI review: 18%" color="success" variant="outlined" />
                  <Chip label="Time-to-first-model: < 1 day" color="primary" variant="outlined" />
                  <Chip label="Support: 2h P1" color="default" variant="outlined" />
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}
              >
                <Typography variant="h6" gutterBottom>
                  Prep checklist (to fast-track)
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">• Your target symbols & timeframes</Typography>
                  <Typography variant="body2">• KPIs (Sharpe, DD, win rate, latency)</Typography>
                  <Typography variant="body2">• Data access mode (API/file)</Typography>
                  <Typography variant="body2">• Decision on live vs. paper trading</Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}

export default DemoPage;

